import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import querystring from 'querystring';
import moment from 'moment';

import { Spinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { GET_BOOKINGS_WITH_LUGGAGE, GET_STATIONS } from 'store/queries';
import { ACTION_CODES } from 'constants/errors';
import { historyShape } from 'constants/propTypes';
import BookingSearchTable from './BookingSearchTable';
import BookingSearchSummary from './BookingSearchSummary';
import { Column, Row } from 'components/shared/Layout';

const dateFormat = 'ddd DD MMM YYYY';

const BookingsWithLuggageSearchResults = ({ history }) => {
  const {
    location: { search }
  } = history;

  const { travelDate, stationUIC } = querystring.parse(search.substr(1));

  const { data: { stations } = { stations: [] } } = useQuery(GET_STATIONS, { fetchPolicy: 'cache-only' });
  const {
    data: { bookingsWithLuggage } = {},
    error,
    loading
  } = useQuery(GET_BOOKINGS_WITH_LUGGAGE, {
    variables: { travelDate, stationUIC },
    fetchPolicy: 'network-only'
  });

  if (loading) {
    return (
      <Row height="100vh" data-testid="bookings-with-luggage-loading">
        <Column alignItems="center" justifyContent="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: '/search/booking'
    });
  }

  const bookings = (bookingsWithLuggage || []).map((booking) => {
    const { bookingReference, bookingStatus, trainNumber, passenger, outboundRoute, extra } = booking;

    const departingOn = outboundRoute?.dateTime ? moment(outboundRoute.dateTime).format(dateFormat) : '';
    const coachSeat = passenger?.coach && passenger?.seat ? `${passenger.coach}/${passenger.seat}` : '';

    const extraItems =
      extra?.map((item) => ({
        itemRef: item.itemRef,
        productCode: item.productCode,
        productName: item.productName,
        coachNumber: item.coachNumber,
        trainSeal: item.trainSeal,
        itemSeal: item.itemSeal
      })) || [];

    return {
      reference: bookingReference,
      status: bookingStatus,
      trains: trainNumber ? [trainNumber] : [],
      coachSeat,
      extraItems,
      itemCoach: extraItems.map((item) => ({ itemRef: item.itemRef, itemCoach: item.coachNumber ?? '--' })),
      trainSeal: extraItems.map((item) => ({ itemRef: item.itemRef, trainSeal: item.trainSeal ?? '--' })),
      itemSeal: extraItems.map((item) => ({ itemRef: item.itemRef, itemSeal: item.itemSeal ?? '--' })),
      customer: {
        name: `${passenger?.firstName ?? '--'} ${passenger?.lastName ?? '--'}`,
        email: passenger?.email ?? '--',
        phoneNumber: passenger?.phoneNumber ?? '--'
      },
      firstName: passenger?.firstName,
      lastName: passenger?.lastName,
      departingOn,
      departingFrom: outboundRoute?.origin,
      departingTo: outboundRoute?.destination
    };
  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <BookingSearchSummary
            data-testid="booking-search-summary"
            noOfPnrsFound={bookings.length}
            departureDate={travelDate}
            departureDateFormat="DD MMM YYYY"
            departureStation={stations?.find(({ uic }) => uic === stationUIC)?.name || ''}
          />
        </div>
      </div>
      {Boolean(bookings.length) && (
        <BookingSearchTable
          data-testid="booking-search-table"
          bookings={bookings}
          defaultSortKey="departingOn"
          columnsLuggage={true}
          history={history}
          location={history.location}
        />
      )}
    </>
  );
};

BookingsWithLuggageSearchResults.propTypes = {
  history: historyShape,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  })
};

export default BookingsWithLuggageSearchResults;
